import { parseDateHeader, recordSample } from './clock';
import { resetUserLogin } from './query-status';
import { authTokenStore, clearAuthToken, getAuthToken } from '@/client/store/auth-token-store';
import { ApiErrorCode, ApiResponse, createApi } from '@/libs/sdk/api';
import { t } from 'i18next';

export const apiUrl = import.meta.env.VITE_API_URL;
// {code: int, reason: string, msg: string, data: {}}

// Check if mock mode is enabled
const isMockMode = import.meta.env.VITE_ENABLE_MOCK === 'true';

// Ky keeps normalized options stable across Request clones and ordinary retries.
// Capture once so an old request can neither adopt nor invalidate a new session.
const requestSessions = new WeakMap<object, { state: ReturnType<typeof authTokenStore.getState>; token: string | null }>();
// Local send time of the latest attempt, for the server clock sample (utils/clock.ts).
const sentAt = new WeakMap<object, number>();

export const api = createApi({
  prefixUrl: apiUrl,
  timeout: 15e3,
  // One retry for a GET on 408/5xx; a 429 and other 4xx are not retried here.
  // The afterResponse hook rejects error responses as Error({ cause: status }),
  // which ky retries whatever the status, so shouldRetry checks that status too.
  // A cause other than 408 or an integer from 500 to 599 gives no retry. No cause
  // (network error) keeps the ky default. A Retry-After header delays the retry
  // by 5 s at most (the ky default has no limit).
  retry: {
    limit: 1,
    methods: ['get'],
    statusCodes: [408, 500, 502, 503, 504],
    maxRetryAfter: 5_000,
    shouldRetry: ({ error: { cause } }) =>
      cause === undefined
        ? undefined
        : cause === 408 || (typeof cause === 'number' && Number.isInteger(cause) && cause >= 500 && cause <= 599),
  },
  mode: 'cors',
  credentials: 'same-origin',
  hooks: {
    beforeRequest: [
      async (request, options) => {
        sentAt.set(options, Date.now());
        if (!isMockMode) {
          let session = requestSessions.get(options);
          if (!session) {
            session = { state: authTokenStore.getState(), token: getAuthToken() };
            requestSessions.set(options, session);
          }
          if (session.token) request.headers.set('Authorization', `Bearer ${session.token}`);
        }
        // No global BaseTokenAddress header: multi-base tenants serve mixed
        // lists; per-market calls pass an explicit baseTokenAddress param.
      },
    ],
    afterResponse: [
      // First, so no rejecting hook skips it. Successful JSON only: an SSE stream's Date is its
      // connect time, and a file download can come from the browser cache with an old Date. The
      // header is checked only after that filter, because the clock logs a header it rejects.
      (_request, options, response) => {
        const sentMs = sentAt.get(options);
        if (!response.ok || sentMs === undefined || !response.headers.get('content-type')?.includes('application/json'))
          return;
        const header = response.headers.get('Date');
        const serverDate = parseDateHeader(header);
        if (serverDate !== undefined)
          recordSample({ serverDateMs: serverDate * 1000, sentMs, receivedMs: Date.now(), header });
      },
      async (request, options, response) => {
        let msg = null;
        if (response.status === 401 || response.status === 403) {
          msg = t('error.unauthorized');
          // Ky clones Request between hooks; the sent Authorization header
          // preserves ownership even when an older HTTP request cannot abort.
          const session = requestSessions.get(options);
          const token = getAuthToken();
          if (
            token &&
            session?.state === authTokenStore.getState() &&
            session.token === token &&
            request.headers.get('Authorization') === `Bearer ${token}`
          ) {
            clearAuthToken();
            await resetUserLogin();
          }
        } else if (!response.ok) {
          try {
            const body = (await response.json()) as ApiResponse;
            if (body.code !== 0) msg = body.msg;
            switch (body.code) {
              case ApiErrorCode.ErrBindSelfInviteCode:
                msg = t('you-cannot-use-your-own-invite-code');
                break;
            }
          } catch {
            if (response.status >= 500) {
              msg = t('error.server-error');
            } else if (response.status >= 400) {
              msg = response.statusText;
            }
          }
        }
        if (msg) return Promise.reject(new Error(msg, { cause: response.status }));
      },
    ],
    beforeError: [
      error => {
        if (
          error.name === 'TypeError' &&
          (error.message.includes('Failed to fetch') || error.message.includes('Network request failed'))
        ) {
          error.message = t('error.network-disconnected');
        } else if (error.name === 'AbortError') {
          error.message = t('error.request-timeout');
        }
        return error;
      },
    ],
  },
});
